import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ledgerApi } from "../lib/ledgerApi";
import StatCard from "../components/StatCard";
import StatusBadge from "../components/StatusBadge";
import HashString from "../components/HashString";
import { ArrowUpRight, Boxes, ChevronRight } from "lucide-react";

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString();
};
const relTime = (iso) => {
  if (!iso) return "—";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(true);
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    let alive = true;
    const tickStats = async () => {
      try {
        const [s, b] = await Promise.all([ledgerApi.stats(), ledgerApi.listBlocks(6)]);
        if (alive) {
          setStats(s);
          setBlocks(b);
        }
      } catch {}
    };
    const tickVerify = async () => {
      try {
        setVerifying(true);
        const v = await ledgerApi.verify();
        if (alive) setVerify(v);
      } catch {} finally {
        if (alive) setVerifying(false);
      }
    };
    tickStats();
    tickVerify();
    const i1 = setInterval(tickStats, 3000);
    const i2 = setInterval(tickVerify, 5000);
    return () => {
      alive = false;
      clearInterval(i1);
      clearInterval(i2);
    };
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-10 md:py-14">
      {/* Hero */}
      <div className="mb-10 md:mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">
            / Ledger · Control Room
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium">
            NeoPay <span className="text-muted-foreground">cryptographic ledger</span>
          </h1>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-xl leading-relaxed">
            Hash-chained, ECDSA-signed, Merkle-rooted blocks sealed every 2 seconds.
            Single-sealer mode for Phase 0 — validator federation arrives in Phase 6.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Chain integrity</span>
          <StatusBadge
            status={verify?.status}
            loading={verifying && !verify}
            testid="chain-status-badge"
          />
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard
          label="Total blocks"
          value={stats ? stats.total_blocks.toLocaleString() : "—"}
          sub={stats ? `Latest: #${stats.latest_block_number}` : ""}
          mono
          testid="stat-total-blocks"
        />
        <StatCard
          label="Total transactions"
          value={stats ? stats.total_transactions.toLocaleString() : "—"}
          sub={stats ? `Pending: ${stats.pending_transactions}` : ""}
          mono
          testid="stat-total-txns"
        />
        <StatCard
          label="Total NEO supply"
          value={stats ? `${stats.total_supply}` : "—"}
          sub="NEO (genesis allocations)"
          mono
          testid="stat-neo-supply"
        />
        <StatCard
          label="Latest block time"
          value={stats ? relTime(stats.latest_block_time) : "—"}
          sub={stats?.latest_block_time ? fmtTime(stats.latest_block_time) : ""}
          testid="stat-latest-block-time"
        />
      </div>

      {/* Verify details */}
      {verify && verify.status === "TAMPERED" && (
        <div
          data-testid="verify-tampered-alert"
          className="mt-6 border border-status-tampered/50 bg-status-tampered/10 p-5 rounded-sm"
        >
          <div className="text-status-tampered font-mono text-xs uppercase tracking-widest mb-2">
            ! Tamper detected
          </div>
          <div className="text-sm">
            Bad blocks: {verify.bad_blocks.map((n) => `#${n}`).join(", ")}
          </div>
          <ul className="mt-3 text-xs font-mono text-muted-foreground space-y-1">
            {verify.details
              .filter((d) => !d.ok)
              .map((d) => (
                <li key={d.block_number}>
                  block #{d.block_number} → {d.reason}
                </li>
              ))}
          </ul>
        </div>
      )}

      {/* Recent blocks */}
      <section className="mt-12">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Boxes className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <h2 className="text-lg font-medium">Recent blocks</h2>
          </div>
          <Link
            to="/blocks"
            data-testid="link-all-blocks"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            View all <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} />
          </Link>
        </div>

        <div className="border border-border rounded-sm divide-y divide-border bg-card">
          {blocks.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground">No blocks yet.</div>
          )}
          {blocks.map((b) => (
            <Link
              key={b.block_number}
              to={`/blocks/${b.block_number}`}
              data-testid={`block-row-${b.block_number}`}
              className="p-4 grid grid-cols-12 gap-3 items-center hover:bg-muted/40 transition-colors"
            >
              <div className="col-span-2 sm:col-span-1 font-mono text-sm">#{b.block_number}</div>
              <div className="col-span-7 sm:col-span-7 min-w-0">
                <HashString value={b.block_hash} label="Block hash" />
                <div className="mt-1 text-[11px] text-muted-foreground font-mono">
                  {b.tx_count} tx · {fmtTime(b.timestamp)}
                </div>
              </div>
              <div className="col-span-3 sm:col-span-3 text-xs text-muted-foreground hidden sm:block truncate font-mono">
                merkle: {b.merkle_root.slice(0, 12)}…
              </div>
              <div className="col-span-3 sm:col-span-1 text-right text-muted-foreground">
                <ChevronRight className="w-4 h-4 inline" strokeWidth={1.5} />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
